import { useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  filterCompanyRFC,
  filterLegalEntityName,
  filterNameInput,
  handlePasteWithoutAccents,
} from "utils/inputFilters";
import { removeAccents } from "utils/utils";
import {
  validateCompanyRFC,
  validatePhoneNotAdvisor,
  validatePhoneNumber,
} from "utils/validators/generalValidators";
import { validateName } from "utils/validators/tenantFormValidators";

import { useCreateTenant } from "hooks/useCreateTenant";

import { Button } from "js/components/ui/button";
import { DialogFooter } from "js/components/ui/dialog";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { Separator } from "js/components/ui/separator";

import { NewTenant, TenantRegistrationProps } from "../types";
import { CountryCodeSelect } from "./CountryCodeSelect";

export const TenantRegistration = ({
  tenantSheetStep,
  newTenant,
  setNewTenant,
  tenantType,
  onTenantCreated,
  onError,
  onCancel,
}: TenantRegistrationProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const emailInputRef = useRef<EmailInputRef>(null);

  const { userData } = useSelector((state: any) => state.entities.user);

  const createTenantMutation = useCreateTenant();

  const handleInputChange = (field: keyof NewTenant, value: string) => {
    setNewTenant((prev: NewTenant) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing (onBlur will re-validate)
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");
    // Limit to 10 digits for Mexican phone numbers
    return cleaned.slice(0, 10);
  };

  const validateField = async (
    field: string,
    value: any,
  ): Promise<string | null> => {
    try {
      switch (field) {
        case "name":
        case "first_surname":
        case "second_surname":
          if (field === "second_surname" && !value) return null; // Optional field
          await validateName(null, value);
          break;
        case "email":
          // Email validation is handled by EmailInput component
          break;
        case "phone_number":
          await validatePhoneNumber(null, value);
          await validatePhoneNotAdvisor(userData, value);
          break;
        case "company_rfc":
          if (tenantType === "moral") {
            await validateCompanyRFC(null, value);
          }
          break;
        case "legal_entity_name":
          if (tenantType === "moral") {
            const alphanumeric120Regex = /^[a-zA-Z0-9&ñÑ\s]{1,120}$/;
            if (!value || !alphanumeric120Regex.test(value)) {
              return "Debe contener de 1 a 120 caractéres alfanuméricos";
            }
          }
          break;
      }
      return null;
    } catch (error: any) {
      return error.message;
    }
  };

  const handleBlur = async (field: keyof NewTenant) => {
    const value = newTenant[field];
    const error = await validateField(field, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  // Basic format validation for immediate feedback
  const hasBasicFormatErrors = (): boolean => {
    // Check phone number format while typing
    if (newTenant.phone_number && newTenant.phone_number.length > 0) {
      const phoneRegex = /^\d{10}$/;
      if (
        newTenant.phone_number.length === 10 &&
        !phoneRegex.test(newTenant.phone_number)
      ) {
        return true;
      }
      // If less than 10 digits, consider it incomplete (not valid)
      if (newTenant.phone_number.length < 10) {
        return true;
      }
    }

    // Check company RFC format for moral persons
    if (
      tenantType === "moral" &&
      newTenant.company_rfc &&
      newTenant.company_rfc.trim() !== ""
    ) {
      const rfcRegex =
        /^([A-ZÑ\x26]{3}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
      if (!rfcRegex.test(newTenant.company_rfc)) {
        return true;
      }
    }

    return false;
  };

  // Check if form is valid (has required fields, no errors, and no basic format issues)
  const isFormValid = (): boolean => {
    const baseFieldsValid =
      !!newTenant.name &&
      !!newTenant.first_surname &&
      !!newTenant.email &&
      !!newTenant.phone_number;

    if (tenantType === "moral") {
      const allFieldsPresent =
        baseFieldsValid &&
        !!newTenant.company_rfc &&
        !!newTenant.legal_entity_name;
      return (
        allFieldsPresent &&
        Object.keys(errors).length === 0 &&
        !hasBasicFormatErrors()
      );
    }

    return (
      baseFieldsValid &&
      Object.keys(errors).length === 0 &&
      !hasBasicFormatErrors()
    );
  };

  // Handle tenant submission
  const handleSubmit = async () => {
    // Validate email using EmailInput ref
    const emailIsValid = await emailInputRef.current?.validate();
    if (!emailIsValid) {
      return;
    }

    // Validate all fields before submitting
    const fieldsToValidate: (keyof NewTenant)[] = [
      "name",
      "first_surname",
      "phone_number",
    ];

    if (newTenant.second_surname?.trim()) {
      fieldsToValidate.push("second_surname");
    }

    if (tenantType === "moral") {
      fieldsToValidate.push("company_rfc", "legal_entity_name");
    }

    // Run validation on all fields
    const validationErrors: Record<string, string> = {};
    for (const field of fieldsToValidate) {
      const error = await validateField(field, newTenant[field]);
      if (error) {
        validationErrors[field] = error;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Double-check validation before submitting
    if (!isFormValid()) {
      return;
    }

    // Transform NewTenant to API payload format
    const payload: any = {
      name: removeAccents(newTenant.name),
      first_surname: removeAccents(newTenant.first_surname),
      phone_number: newTenant.phone_number,
      dial_code: newTenant.dial_code,
      email: newTenant.email,
      legal_type: tenantType === "fisica" ? "1" : "2",
    };

    // Only include second_surname if it has a value
    if (newTenant.second_surname?.trim()) {
      payload.second_surname = removeAccents(newTenant.second_surname);
    }

    // Add company fields if it's a moral person
    if (tenantType === "moral") {
      payload.rfc = newTenant.company_rfc;
      payload.legal_entity_name = removeAccents(newTenant.legal_entity_name);
    }

    createTenantMutation.mutate(payload, {
      onSuccess: (response) => {
        if (onTenantCreated) {
          onTenantCreated(response);
        }
      },
      onError: (error) => {
        console.error("Error creating tenant:", error);
        const errorMessage =
          "Error al crear el inquilino. Por favor intente de nuevo.";

        if (onError) {
          onError(errorMessage);
        } else {
          alert(errorMessage);
        }
      },
    });
  };

  if (tenantSheetStep === 1) {
    return (
      <div className="space-y-8">
        <div className="space-y-6">
          <div className="space-y-6">
            {tenantType === "moral" && (
              <h4 className="text-base font-semibold text-gray-900">
                Datos del Representante Legal
              </h4>
            )}

            {/* Personal Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700 mb-2 block"
                  >
                    Nombre *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={newTenant.name}
                    onChange={(e) => {
                      const filtered = filterNameInput(e.target.value);
                      handleInputChange("name", filtered.toUpperCase());
                    }}
                    onPaste={(e) => {
                      handlePasteWithoutAccents(e, filterNameInput, (value) => {
                        handleInputChange("name", value.toUpperCase());
                      });
                    }}
                    onBlur={() => handleBlur("name")}
                    className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.name ? "border-red-500" : ""}`}
                    placeholder="Ingresa el nombre"
                  />
                  {errors.name && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="first_surname"
                    className="text-sm font-medium text-gray-700 mb-2 block"
                  >
                    Apellido Paterno *
                  </Label>
                  <Input
                    id="first_surname"
                    type="text"
                    value={newTenant.first_surname}
                    onChange={(e) => {
                      const filtered = filterNameInput(e.target.value);
                      handleInputChange(
                        "first_surname",
                        filtered.toUpperCase(),
                      );
                    }}
                    onPaste={(e) => {
                      handlePasteWithoutAccents(e, filterNameInput, (value) => {
                        handleInputChange("first_surname", value.toUpperCase());
                      });
                    }}
                    onBlur={() => handleBlur("first_surname")}
                    className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.first_surname ? "border-red-500" : ""}`}
                    placeholder="Ingresa el apellido paterno"
                  />
                  {errors.first_surname && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.first_surname}
                    </span>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="second_surname"
                    className="text-sm font-medium text-gray-700 mb-2 block"
                  >
                    Apellido Materno
                  </Label>
                  <Input
                    id="second_surname"
                    type="text"
                    value={newTenant.second_surname}
                    onChange={(e) => {
                      const filtered = filterNameInput(e.target.value);
                      handleInputChange(
                        "second_surname",
                        filtered.toUpperCase(),
                      );
                    }}
                    onPaste={(e) => {
                      handlePasteWithoutAccents(e, filterNameInput, (value) => {
                        handleInputChange(
                          "second_surname",
                          value.toUpperCase(),
                        );
                      });
                    }}
                    onBlur={() => handleBlur("second_surname")}
                    className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.second_surname ? "border-red-500" : ""}`}
                    placeholder="Ingresa el apellido materno"
                  />
                  {errors.second_surname && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.second_surname}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <EmailInput
                    ref={emailInputRef}
                    value={newTenant.email}
                    onChange={(value) => handleInputChange("email", value)}
                    label="Correo Electrónico"
                    placeholder="correo@ejemplo.com"
                    required
                    userData={userData}
                    showIcon
                    showStatus
                    className="h-11"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="phone_number"
                    className="text-sm font-medium text-gray-700 mb-2 block"
                  >
                    Teléfono *
                  </Label>
                  <div className="flex gap-2">
                    <div className="w-32">
                      <CountryCodeSelect
                        value={newTenant.dial_code}
                        onValueChange={(value) =>
                          handleInputChange("dial_code", value)
                        }
                      />
                    </div>
                    <Input
                      id="phone_number"
                      type="tel"
                      value={newTenant.phone_number}
                      onChange={(e) =>
                        handleInputChange(
                          "phone_number",
                          formatPhoneNumber(e.target.value),
                        )
                      }
                      onBlur={() => handleBlur("phone_number")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 flex-1 ${errors.phone_number ? "border-red-500" : ""}`}
                      placeholder="1234567890"
                      maxLength={10}
                    />
                  </div>
                  {errors.phone_number && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {errors.phone_number}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {tenantType === "moral" && (
              <>
                <Separator className="my-6" />
                <h4 className="text-base font-semibold text-gray-900">
                  Datos de la Empresa
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="company_rfc"
                      className="text-sm font-medium text-gray-700 mb-2 block"
                    >
                      RFC de la Empresa *
                    </Label>
                    <Input
                      id="company_rfc"
                      type="text"
                      value={newTenant.company_rfc}
                      onChange={(e) =>
                        handleInputChange(
                          "company_rfc",
                          filterCompanyRFC(e.target.value),
                        )
                      }
                      onBlur={() => handleBlur("company_rfc")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.company_rfc ? "border-red-500" : ""}`}
                      placeholder="ABC123456789"
                      maxLength={13}
                    />
                    {errors.company_rfc && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {errors.company_rfc}
                      </span>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="legal_entity_name"
                      className="text-sm font-medium text-gray-700 mb-2 block"
                    >
                      Razón Social *
                    </Label>
                    <Input
                      id="legal_entity_name"
                      type="text"
                      value={newTenant.legal_entity_name}
                      onChange={(e) => {
                        const filtered = filterLegalEntityName(e.target.value);
                        handleInputChange(
                          "legal_entity_name",
                          filtered.toUpperCase(),
                        );
                      }}
                      onPaste={(e) => {
                        handlePasteWithoutAccents(
                          e,
                          filterLegalEntityName,
                          (value) => {
                            handleInputChange(
                              "legal_entity_name",
                              value.toUpperCase(),
                            );
                          },
                        );
                      }}
                      onBlur={() => handleBlur("legal_entity_name")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.legal_entity_name ? "border-red-500" : ""}`}
                      placeholder="Nombre de la empresa"
                      maxLength={120}
                    />
                    {errors.legal_entity_name && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {errors.legal_entity_name}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <DialogFooter className="flex flex-row items-center pt-4 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="h-11 px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={createTenantMutation.isPending}
          >
            Cancelar
          </Button>

          <div className="flex-1"></div>

          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || createTenantMutation.isPending}
            className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createTenantMutation.isPending ? (
              <>Creando...</>
            ) : (
              <>Registrar Inquilino</>
            )}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return null;
};
